import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_API_KEY = 'surveyTemplate/:industry/filter';

export interface ISearchSurveyTemplatesQueryParams {
	industryName: string;
	searchTerm: string;
	enabled?: boolean;
}

/**
 * Search survey templates within a specific industry by name, summary, or keywords.
 * Returns lightweight template cards matching the search criteria.
 *
 * @param params - Query parameters including industry name and search term
 * @returns React Query result with array of matching survey template cards
 */
export function useSearchSurveyTemplatesQuery(params: ISearchSurveyTemplatesQueryParams) {
	const { industryName, searchTerm, enabled = true } = params;
	const userSession = useUserSession();

	return useQuery({
		queryFn: () => {
			return userSession.webServiceHelper.callAsync<Api.ISurveyTemplateCard[]>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: { search: searchTerm },
					urlPath: `surveytemplate/${industryName}/filter`,
				}),
				'GET'
			);
		},
		queryKey: [BASE_API_KEY, 'search', industryName, searchTerm],
		enabled: enabled && !!industryName && !!searchTerm,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
}

export const invalidateSearchSurveyTemplatesQuery = (industryName?: string) => {
	if (industryName) {
		reactQueryClient.invalidateQueries({ queryKey: [BASE_API_KEY, 'search', industryName] });
	} else {
		reactQueryClient.invalidateQueries({ queryKey: [BASE_API_KEY, 'search'] });
	}
};
